import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { RequireAuth, RequireAdmin } from "@/components/RequireAuth";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import StripeSignup from "@/pages/StripeSignup";
import StripeSignin from "@/pages/StripeSignin";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import MyAccount from "@/pages/MyAccount";
import AdminDashboard from "@/pages/AdminDashboard";
import ToolFrame from "@/pages/ToolFrame";
import PubGlotPage from "@/pages/PubGlotPage";
import Landing from "@/pages/Landing";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Refund from "@/pages/Refund";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutCancel from "@/pages/CheckoutCancel";
import Checkout from "@/pages/Checkout";
import OrderSummary from "@/pages/OrderSummary";
import useAnalytics from "@/hooks/useAnalytics";
import usePixels from "@/hooks/usePixels";
import "@/App.css";

// Tiny wrapper so we can call hooks that need the router context
function AppShell() {
  useAnalytics();
  usePixels();
  return (
    <Routes>
      {/* Stripe-customer auth (linked from sales page) */}
      <Route path="/stripe-signup" element={<StripeSignup />} />
      <Route path="/stripe-signin" element={<StripeSignin />} />
      {/* License-code auth (hidden, distributed manually) */}
      <Route path="/signup-code" element={<Signup />} />
      <Route path="/signin-code" element={<Login />} />
      {/* Legacy redirects so any old links continue to work */}
      <Route path="/signup" element={<Navigate to="/stripe-signup" replace />} />
      <Route path="/login" element={<Navigate to="/stripe-signin" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refund" element={<Refund />} />
      <Route path="/checkout/success" element={<RequireAuth><CheckoutSuccess /></RequireAuth>} />
      <Route path="/checkout/cancel" element={<CheckoutCancel />} />
      <Route path="/order-summary" element={<RequireAuth><OrderSummary /></RequireAuth>} />
      <Route path="/checkout/:tierId" element={<RequireAuth><Checkout /></RequireAuth>} />
      <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
      <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
      <Route path="/account" element={<RequireAuth><MyAccount /></RequireAuth>} />
      <Route path="/tools/pubglot" element={<RequireAuth><PubGlotPage /></RequireAuth>} />
      <Route path="/tools/:slug" element={<RequireAuth><ToolFrame /></RequireAuth>} />
      <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" richColors />
    </AuthProvider>
  );
}
